import React, { useState, useRef, useEffect } from "react";
import { ColumnDef } from "./types";
import "./ColumnChooser.scss";

interface ColumnChooserProps<T = unknown> {
  columns: ColumnDef<T>[];
  onColumnVisibilityChange: (columnId: string, visible: boolean) => void;
}

export function ColumnChooser<T = unknown>({
  columns,
  onColumnVisibilityChange,
}: ColumnChooserProps<T>) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const visibleCount = columns.filter((col) => col.visible !== false).length;
  const totalCount = columns.length;

  const handleToggleColumn = (columnId: string) => {
    const column = columns.find((col) => col.id === columnId);
    if (column) {
      onColumnVisibilityChange(columnId, column.visible === false);
    }
  };

  const handleShowAll = () => {
    columns.forEach((col) => {
      if (col.visible === false) {
        onColumnVisibilityChange(col.id, true);
      }
    });
  };

  const handleHideAll = () => {
    columns.forEach((col) => {
      if (col.visible !== false) {
        onColumnVisibilityChange(col.id, false);
      }
    });
  };

  return (
    <div className="column-chooser" ref={dropdownRef}>
      <button
        className="column-chooser-button"
        onClick={() => setIsOpen(!isOpen)}
        title="Choose columns to display"
      >
        <span className="column-icon">⚙</span>
        <span className="column-count">
          {visibleCount}/{totalCount} columns
        </span>
      </button>

      {isOpen && (
        <div className="column-chooser-dropdown">
          <div className="column-chooser-header">
            <span className="column-chooser-title">Show/Hide Columns</span>
            <div className="column-chooser-actions">
              <button className="action-button" onClick={handleShowAll}>
                Show All
              </button>
              <button className="action-button" onClick={handleHideAll}>
                Hide All
              </button>
            </div>
          </div>
          <div className="column-chooser-list">
            {columns.map((column) => (
              <label key={column.id} className="column-chooser-item">
                <input
                  type="checkbox"
                  checked={column.visible !== false}
                  onChange={() => handleToggleColumn(column.id)}
                />
                <span className="column-label">{column.header}</span>
                {/*
                  The column's PATH, always. A page reaches through the model's
                  auto-joins, and a joined field keeps the label of the column on
                  the table it lives on — so the Monitors chooser listed "Name"
                  twice and "Uid" three times with nothing to tell them apart
                  (glen, 2026-09-10). The id IS the path the SELECT uses
                  ("job.name"), which is the disambiguator. Shown on every entry
                  rather than only on collisions: an entry whose text depends on
                  what else is in the list is harder to learn than a uniform one.
                */}
                <span className="column-path" title={column.id}>
                  {column.id}
                </span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
